import { driver, Driver, DriveStep, Config } from 'driver.js';
import 'driver.js/dist/driver.css';
import { FeatureTourDefinition, FeatureTourStep } from '../types/featureTour';
import { featureTourPersistence } from './featureTourPersistence';
import store from '@/store';
import { FeatureTourState } from '@/store/featureTourStore';
import { analytics, EVENTS } from '@/services/analytics';

// Re-exported for components that previously imported from this file.
export type FeatureTourServiceState = FeatureTourState;

const tourState = (): FeatureTourState => store.state.featureTour as FeatureTourState;
const patch = (payload: Partial<FeatureTourState>) => {
	store.commit('featureTour/PATCH', payload);
};
const resetState = () => {
	store.commit('featureTour/RESET');
};

let driverInstance: Driver | null = null;
// Track the direction of the most recent navigation so the auto-skip logic
// (for steps whose target isn't visible) moves the user the way they intended,
// not always forward. Without this, clicking Back into an invisible step would
// immediately bounce forward, making Back appear broken.
let lastNavDirection: 'forward' | 'backward' = 'forward';
// Indices we've discovered to be unreachable for the current run. Populated as
// the auto-skip logic encounters hidden targets, then used to renumber the
// visible steps shown in the tour card.
let skippedStepIndices: Set<number> = new Set();
// Cleanup function for any per-step listeners we attach (e.g. the
// dismissOnTargetClick handler). Always called before a new step starts and
// during teardown so listeners don't leak across steps or after the tour ends.
let currentStepCleanup: (() => void) | null = null;

const runCurrentStepCleanup = () => {
	if (currentStepCleanup) {
		try {
			currentStepCleanup();
		} catch (e) {
			// Swallow — cleanup must never throw and brick the tour.
		}
		currentStepCleanup = null;
	}
};

export const featureTourService = {
	get state () {
		return tourState();
	},

	async start (tour: FeatureTourDefinition, startAtStep: number = 0) {
		if (tourState().isActive) {
			this.stop();
		}

		patch({
			activeTour: tour,
			isActive: true,
			displayStepIndex: 0,
		});
		lastNavDirection = 'forward';

		// Pre-evaluate canShow for every step and PRE-FILTER the list before
		// handing it to driver.js. The earlier "skip during onHighlightStarted"
		// approach worked for state but caused two problems:
		//   1. Rapid synchronous moveNext() calls left driver.js's internal
		//      stage position stuck at the default 10x10 in screen center,
		//      so the spotlight didn't actually highlight the next visible
		//      step's target.
		//   2. The card briefly rendered with no rect, then snapped into place.
		// Filtering up front means driver.js only ever navigates between steps
		// that should display — its own animation/spotlight stays in sync.
		//
		// Trade-off: the visibleSteps list is FROZEN for the duration of the
		// tour. If a canShow predicate flips mid-tour (e.g. boardHasTools()
		// becomes true after the user adds their first tool while the tour is
		// still on the welcome step), the originally-filtered steps don't
		// retroactively appear. This is an intentional choice — re-evaluating
		// canShow per-step would mean rebuilding visibleSteps and restarting
		// driver.js, which would jar the user out of their current step.
		// The runtime visibility check inside onHighlightStarted (see below)
		// is the safety net for the inverse case: a step that PASSED canShow
		// but whose target is no longer visible at the moment we'd render it.
		skippedStepIndices = new Set();
		for (let i = 0; i < tour.steps.length; i += 1) {
			const step = tour.steps[i];
			if (!step.canShow) continue;
			try {
				const ok = await step.canShow();
				if (!ok) skippedStepIndices.add(i);
			} catch (e) {
				skippedStepIndices.add(i);
			}
		}

		// Build (step, originalIndex) tuples for the steps that survived. We
		// keep originalIndex around so activeStepIndex still reflects
		// the source step index — useful for callers that key off step ids.
		const visibleSteps: { step: FeatureTourStep; originalIndex: number }[] = [];
		tour.steps.forEach((step, originalIndex) => {
			if (!skippedStepIndices.has(originalIndex)) {
				visibleSteps.push({ step, originalIndex });
			}
		});
		patch({ displayTotalSteps: visibleSteps.length });

		if (visibleSteps.length === 0) {
			// Nothing to show on this context — bail without starting driver.js.
			this.handleTourEnd();
			return;
		}

		const driverSteps: DriveStep[] = visibleSteps.map(({ step, originalIndex }, visIndex) => ({
			element: typeof step.target === 'function' ? step.target : step.target,
			onHighlightStarted: async (el, s, { driver }) => {
				if (step.beforeEnter) {
					await step.beforeEnter();
				}

				// Allow beforeEnter-triggered UI transitions (drawers, etc.) to settle
				// before resolving the target. Steps that survived canShow can still
				// be runtime-invisible (e.g. a tool that doesn't expose a given field),
				// so we poll briefly and skip the step in-place if nothing shows up.
				const POLL_MS = 50;
				const MAX_WAIT_MS = 600;
				const start = Date.now();
				const findResolved = (): HTMLElement | null => this.resolveStepTarget(step, el as HTMLElement | null);
				const waitForTarget = async (): Promise<HTMLElement | null> => {
					return new Promise((resolve) => {
						const tick = () => {
							const found = findResolved();
							if (found && this.isElementVisible(found)) {
								resolve(found);
								return;
							}
							if (Date.now() - start >= MAX_WAIT_MS) {
								resolve(found && this.isElementVisible(found) ? found : null);
								return;
							}
							setTimeout(tick, POLL_MS);
						};
						tick();
					});
				};

				const resolved = await waitForTarget();

				if (!resolved || !this.isElementVisible(resolved)) {
					// Runtime visibility failure on a step that passed canShow.
					// Skip in the direction the user is navigating so Back into
					// a hidden step keeps moving backward instead of bouncing
					// forward to the step they just left.
					if (lastNavDirection === 'backward') {
						if (visIndex > 0) {
							driver.movePrevious();
						} else {
							driver.moveNext();
						}
					} else if (driver.isLastStep()) {
						driver.destroy();
					} else {
						driver.moveNext();
					}
					return;
				}

				// Tear down any listener from the previous step before wiring
				// up a new one. (next()/previous() also call this, but during
				// the visibility-skip path control jumps straight here.)
				runCurrentStepCleanup();

				patch({
					activeStepIndex: originalIndex,
					activeStep: step,
					targetElement: resolved,
					displayStepIndex: visIndex + 1,
				});
				this.updateTargetRect();

				// Driver.js's spotlight stage position can lag behind a step
				// transition when the previous step's element was much larger
				// than the new one (e.g. the whole drawer → a small button).
				// It only re-derives the path on its resize listener, so we
				// nudge it on the next frame to force a recompute now that the
				// active element is fully in place.
				requestAnimationFrame(() => {
					window.dispatchEvent(new Event('resize'));
				});

				// "Click the highlighted thing to dismiss" — once the user
				// takes the taught action, the tour card just gets in the way
				// of whatever follow-on UI opened (a modal, drawer, etc.).
				// Also resolves "companion targets" — additional places where
				// the same action lives — and pulses them so users notice both.
				const dismissOnTargetClick = !!step.dismissOnTargetClick;
				const companionSelectors: string[] = step.companionTargets || [];
				const companionEls: HTMLElement[] = [];
				companionSelectors.forEach((sel) => {
					try {
						const el = document.querySelector(sel) as HTMLElement | null;
						if (el && el !== resolved) {
							el.classList.add('feature-tour-companion-highlight');
							companionEls.push(el);
						}
					} catch (e) {
						// Ignore bad selectors — the tour shouldn't crash on a
						// typo in the step config.
					}
				});

				const targets: HTMLElement[] = dismissOnTargetClick
					? [resolved, ...companionEls]
					: [];
				const onTargetClick = () => {
					this.complete();
				};
				targets.forEach((el) => {
					el.addEventListener('click', onTargetClick, true);
				});

				if (dismissOnTargetClick || companionEls.length > 0) {
					currentStepCleanup = () => {
						targets.forEach((el) => {
							el.removeEventListener('click', onTargetClick, true);
						});
						companionEls.forEach((el) => {
							el.classList.remove('feature-tour-companion-highlight');
						});
					};
				}
			},
			onHighlighted: (el) => {
				// Defensive: only update on a real element. Driver.js has been
				// observed firing this with null during transition edge cases
				// and we don't want to clobber the rect onHighlightStarted set.
				if (el) {
					patch({ targetElement: el as HTMLElement });
					this.updateTargetRect();
				}
			},
			popover: {
				// We suppress the default popover by providing no content
				// and using our custom Vue shell instead.
				// CSS in GlobalSCSS hides the default popover.
			}
		}));

		driverInstance = driver({
			animate: true,
			overlayOpacity: 0.75,
			allowClose: false, // We handle close in our UI
			overlayClickBehavior: 'close',
			steps: driverSteps,
			onDestroyed: () => {
				this.handleTourEnd();
			},
		});

		featureTourPersistence.markTourStarted(tour.id, tour.version);
		analytics.track(EVENTS.FEATURE_TOUR_STARTED, {
			tour_id: tour.id,
			tour_version: tour.version,
			total_steps: visibleSteps.length,
		});

		// Translate startAtStep (an original-index value) to the corresponding
		// position in the filtered list. If the requested original index was
		// itself filtered out, snap to the next visible step.
		let driverStartIndex = 0;
		for (let v = 0; v < visibleSteps.length; v += 1) {
			if (visibleSteps[v].originalIndex >= startAtStep) {
				driverStartIndex = v;
				break;
			}
		}
		driverInstance.drive(driverStartIndex);
	},

	stop () {
		// Treat any user-initiated stop (X button, escape, etc.) as a dismiss
		// so the tour doesn't auto-relaunch on the next page load. complete()
		// runs markTourCompleted before this and we don't want to overwrite
		// that with "dismissed".
		const activeTour = tourState().activeTour;
		if (activeTour) {
			const persisted = featureTourPersistence.getTourState(activeTour.id);
			if (persisted?.status !== 'completed') {
				featureTourPersistence.markTourDismissed(activeTour.id, activeTour.version);
				// Frustration signal — where users bail on onboarding tours.
				let abandonedAtStep: number | null = null;
				try {
					abandonedAtStep = driverInstance ? driverInstance.getActiveIndex() ?? null : null;
				} catch (e) { /* driver.js API drift; safe to omit */ }
				analytics.track(EVENTS.FEATURE_TOUR_DISMISSED, {
					tour_id: activeTour.id,
					tour_version: activeTour.version,
					abandoned_at_step: abandonedAtStep,
				});
			}
		}
		if (driverInstance) {
			driverInstance.destroy();
			driverInstance = null;
		}
		this.handleTourEnd();
	},

	next () {
		if (driverInstance) {
			lastNavDirection = 'forward';
			runCurrentStepCleanup();
			if (driverInstance.isLastStep()) {
				this.complete();
			} else {
				driverInstance.moveNext();
			}
		}
	},

	previous () {
		if (driverInstance) {
			lastNavDirection = 'backward';
			runCurrentStepCleanup();
			driverInstance.movePrevious();
		}
	},

	complete () {
		const activeTour = tourState().activeTour;
		if (activeTour) {
			featureTourPersistence.markTourCompleted(activeTour.id, activeTour.version);
			analytics.track(EVENTS.FEATURE_TOUR_COMPLETED, {
				tour_id: activeTour.id,
				tour_version: activeTour.version,
			});
		}
		this.stop();
	},

	updateTargetRect () {
		const targetElement = tourState().targetElement;
		if (targetElement) {
			patch({ targetRect: targetElement.getBoundingClientRect() });
		} else {
			patch({ targetRect: null });
		}
	},

	resolveStepTarget (step: FeatureTourStep, hint: HTMLElement | null = null): HTMLElement | null {
		if (hint) return hint;
		if (typeof step.target === 'function') {
			try {
				return step.target();
			} catch (e) {
				return null;
			}
		}
		try {
			// Tools can render multiple elements with the same data-feature-tour
			// attribute (e.g. tools with several IMG fields all carry
			// data-feature-tour="board-image-input-field"). Prefer the first
			// VISIBLE match rather than blindly taking document order — otherwise
			// the tour would point at a hidden first IMG and the user would see
			// nothing highlighted. Fall back to the first match in DOM order if
			// nothing is currently visible; the surrounding poll loop in
			// onHighlightStarted handles the "not visible yet" case.
			const matches = document.querySelectorAll(step.target);
			for (let i = 0; i < matches.length; i += 1) {
				const el = matches[i] as HTMLElement;
				if (this.isElementVisible(el)) return el;
			}
			return (matches[0] as HTMLElement | null) || null;
		} catch (e) {
			return null;
		}
	},

	isElementVisible (el: HTMLElement): boolean {
		if (!el.isConnected) return false;
		// display: none collapses offset dimensions; getBoundingClientRect
		// agrees, so this catches it. visibility: hidden keeps layout, so
		// dimensions stay non-zero — check computed style explicitly.
		if (el.offsetWidth === 0 && el.offsetHeight === 0) return false;
		if (typeof window !== 'undefined') {
			const computed = window.getComputedStyle(el);
			if (computed.visibility === 'hidden' || computed.display === 'none') return false;
		}
		const rect = el.getBoundingClientRect();
		return rect.width > 0 && rect.height > 0;
	},

	handleTourEnd () {
		runCurrentStepCleanup();
		resetState();
		skippedStepIndices = new Set();
	},

	async maybeAutoStart (tour: FeatureTourDefinition) {
		// Tester override: localStorage RD_FEATURE_TOUR_FORCE=<tourId>[,<tourId>...]
		// bypasses completion and autoLaunch guards so QA can trigger a tour on demand.
		if (featureTourPersistence.isForceTriggered(tour.id)) {
			this.start(tour);
			return true;
		}

		// Don't auto-relaunch a tour the user has already seen — completed,
		// dismissed via the X button, OR merely started (refreshed or
		// navigated away mid-tour). The launcher icon ("?") is the manual
		// replay path; it calls start() directly and bypasses this gate.
		if (featureTourPersistence.hasSeenTour(tour.id, tour.version)) {
			return false;
		}

		let shouldLaunch = false;
		if (typeof tour.autoLaunch === 'function') {
			shouldLaunch = await tour.autoLaunch();
		} else if (typeof tour.autoLaunch === 'boolean') {
			shouldLaunch = tour.autoLaunch;
		}

		if (shouldLaunch) {
			this.start(tour);
			return true;
		}

		return false;
	}
};

// Listen for resize/scroll to keep our custom card in sync with Driver.js spotlight.
// Gated on isActive so we don't pay any rect-computation cost on every page
// scroll/resize during the entire app lifetime when no tour is running. The
// listeners stay attached either way — re-attaching them every tour start would
// cost more (and risk leaks) than the cheap state check below.
if (typeof window !== 'undefined') {
	window.addEventListener('resize', () => {
		if (tourState().isActive) featureTourService.updateTargetRect();
	});
	window.addEventListener('scroll', () => {
		if (tourState().isActive) featureTourService.updateTargetRect();
	}, true);
}
